import { parseCsv } from './validation';
import { ITableInputValue } from '../types';
import React from 'react';
import { Table2 } from '@blueprintjs/table';
import { ISetting } from '@biolibtech/biolib-js';
import { getRandomId, saveFileAs } from '@utils';
import { store } from '../../../../../../state';
import { utf8ToBase64 } from '../../../../../../apiHooks/utils';
import { dataRecordApi } from '@apiHooks';

const escapeCsvValue = (value: string): string =>
    /[,"\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

export const calculateDefaultColumnWidths = (
    columnNames: string[],
    tableRef: React.RefObject<Table2>,
    containerWidth?: number,
    minWidth: number = 100,
    maxWidth: number = 300,
): number[] => {
    let calculatedWidths: number[];

    if (tableRef.current?.locator) {
        calculatedWidths = columnNames.map((_, index) => {
            const defaultWidth = tableRef.current!.locator!.getWidestVisibleCellInColumn(index);
            return Math.max(minWidth, Math.min(defaultWidth, maxWidth));
        });
    } else {
        const charWidth = 8;
        const padding = 32;
        calculatedWidths = columnNames.map((name) => {
            const baseWidth = name.length * charWidth + padding;
            return Math.max(minWidth, Math.min(baseWidth, maxWidth));
        });
    }

    if (containerWidth && columnNames.length > 0) {
        const totalCalculatedWidth = calculatedWidths.reduce((sum, width) => sum + width, 0);

        if (totalCalculatedWidth < containerWidth) {
            const equalWidth = Math.floor(containerWidth / columnNames.length);
            const remainder = containerWidth % columnNames.length;

            return calculatedWidths.map((_, index) => equalWidth + (index < remainder ? 1 : 0));
        }
    }

    return calculatedWidths;
};

export const downloadTableAsCSV = (
    rows: Record<number, Record<number, string>>,
    columns: string[],
    filename?: string,
) => {
    const csvLines: string[] = [];

    if (columns.length > 0) {
        const headerRow = columns
            .map((name) => {
                return escapeCsvValue(name);
            })
            .join(',');
        csvLines.push(headerRow);
    }

    const rowsSorted = Object.entries(rows).sort((a, b) => Number(a[0]) - Number(b[0]));

    for (const [rowIndex, cols] of rowsSorted) {
        const rowValues: string[] = [];
        let hasAnyValue = false;

        for (let i = 0; i < columns.length; i++) {
            const value = (cols as Record<number, string>)[i] || '';
            if (value.trim()) {
                hasAnyValue = true;
            }
            rowValues.push(escapeCsvValue(value));
        }

        if (hasAnyValue) {
            csvLines.push(rowValues.join(','));
        }
    }

    if (csvLines.length > 0) {
        const csvContent = csvLines.join('\n');
        const csvData = new TextEncoder().encode(csvContent);
        saveFileAs(filename || 'table_data.csv', 'text/csv;charset=utf-8', csvData);
    }
};

export const prepareTableParametersAndFiles = (
    setting: ISetting,
    settingValue: ITableInputValue,
    files: Record<string, { data: Uint8Array }>,
): string => {
    const { filename, rows } = settingValue;
    const columns = setting.config?.columns || [];
    const csvLines: string[] = [];

    if (Array.isArray(columns) && columns.length > 0) {
        const headerRow = columns
            .map((col: any) => {
                const name = col.name || '';
                return escapeCsvValue(name);
            })
            .join(',');
        csvLines.push(headerRow);
    }

    const rowsSorted = Object.entries(rows).sort((a, b) => Number(a[0]) - Number(b[0]));

    for (const [rowIndex, cols] of rowsSorted) {
        const rowValues: string[] = [];
        let hasAnyValue = false;

        for (let i = 0; i < Math.max(columns.length, Object.keys(cols).length); i++) {
            const value = (cols as Record<number, string>)[i] || '';
            if (value.trim()) {
                hasAnyValue = true;
            }
            rowValues.push(escapeCsvValue(value));
        }

        if (hasAnyValue) {
            csvLines.push(rowValues.join(','));
        }
    }

    if (csvLines.length > 0) {
        const defaultFilename: string = setting.default_value.split('/').pop() || '';
        const inputFilename =
            filename ??
            `input_${getRandomId(7)}${defaultFilename ? '_' : ''}${defaultFilename.replace('.fasta', '.csv')}`;
        const csvString = csvLines.join('\n');
        files['/'.concat(inputFilename)] = { data: new TextEncoder().encode(csvString) };
        return inputFilename;
    } else {
        return setting.default_value;
    }
};

export function getInitialTableInputValue(prefilledInput?: string, columns?: any[]): ITableInputValue {
    const numColumns = columns?.length || 2;
    const initialRows: Record<number, Record<number, string>> = {};

    for (let i = 0; i < 3; i += 1) {
        initialRows[i] = {};
        for (let j = 0; j < numColumns; j += 1) {
            initialRows[i][j] = '';
        }
    }

    if (prefilledInput) {
        try {
            const parseResult = parseCsv(prefilledInput, { columns });

            for (let i = 0; i < parseResult.records.length; i += 1) {
                const record = parseResult.records[i];
                initialRows[i] = { ...record };
            }
        } catch (error) {
            console.error('Failed to parse prefilled input as CSV. Got error: ', error);
            initialRows[0][0] = prefilledInput;
        }
    }

    return {
        filename: null,
        rows: initialRows,
    };
}

const MAX_FILE_SIZE_BYTES = 1024 * 1024 * 100; // 100MB

export async function loadTableDefaultValue(resourceVersionUuid: string, defaultValue: string): Promise<string> {
    const filePath = defaultValue.replace(/^(assets|app_data):\/\//, '');
    const versionResult = await store.dispatch(
        // @ts-ignore
        dataRecordApi.endpoints.getDataRecordVersion.initiate({
            resourceVersionUuid,
        }),
    );

    if (versionResult.error) {
        throw new Error('Failed to get presigned URL');
    }

    const { size, presigned_download_url } = versionResult.data;

    if (size > MAX_FILE_SIZE_BYTES) {
        throw new Error(`File is too large (${Math.round(size / 1024 / 1024)}MB). Maximum allowed size is 100MB.`);
    }

    const csvUrl = `${location.origin}/proxy/files/v1/data/?upstream_url_base64=${utf8ToBase64(
        presigned_download_url,
    )}&path_base64=${utf8ToBase64(`/${filePath}`)}`;

    const response = await fetch(csvUrl);
    if (response.status !== 200) {
        throw new Error(`Failed to fetch CSV data. Got HTTP status: ${response.status}`);
    }

    const csvData = await response.text();

    return csvData;
}

export function convertExcelToTableInputRows(
    excelFile: { sheets: Record<string, { rows: (string | number)[][] }> },
    columnConfigs?: any[],
    sheetName?: string,
    columnMapping?: Record<number, number>,
    hasHeaders?: boolean,
): Record<number, Record<number, string>> {
    const sheetNames = Object.keys(excelFile.sheets);
    if (sheetNames.length === 0) {
        return {};
    }

    const targetSheet =
        sheetName && excelFile.sheets[sheetName] ? excelFile.sheets[sheetName] : excelFile.sheets[sheetNames[0]];

    const rows: Record<number, Record<number, string>> = {};
    const numColumns = columnConfigs?.length || 2;
    const startRow = hasHeaders ? 1 : 0;

    for (let i = startRow; i < targetSheet.rows.length; i++) {
        const excelRow = targetSheet.rows[i];
        const outputRowIndex = i - startRow;
        rows[outputRowIndex] = {};

        for (let j = 0; j < numColumns; j++) {
            const excelColIndex = columnMapping?.[j] ?? j;
            const value = excelRow[excelColIndex];
            rows[outputRowIndex][j] = value !== undefined && value !== null ? String(value) : '';
        }
    }

    return rows;
}

export function hasNonEmptyRows(rows: Record<number, Record<number, string>> | null | undefined): boolean {
    if (!rows) {
        return false;
    }

    for (const rowKey in rows) {
        const row = rows[Number(rowKey)];
        if (!row) {
            continue;
        }

        for (const colKey in row) {
            const cell = row[Number(colKey)];
            if (cell && cell.trim() !== '') {
                return true;
            }
        }
    }

    return false;
}
